import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { CreateUserCommand, UpdateUserCommand, AssignRoleCommand } from '../../domain/interfaces/IUserRepository';
import { PaginatedResult, PaginationParams } from '../../domain/common/PaginatedResult';
import { UserRole } from '../../domain/value-objects/UserRole';
import { UserManagementService } from '../services/UserManagementService';
import { UserRepository } from '../../infrastructure/repositories/UserRepository';
import { apiClient } from '../../infrastructure/api/ApiClient';
import { notificationService } from '../../infrastructure/notifications';
import { queryKeys } from './queryKeys';
import { CACHE_CONFIG } from './queryClient';
import { UserListItemDto, UserDetailDto } from '../dto/UserDtos';

/**
 * Service instance (singleton)
 */
const userRepository = new UserRepository(apiClient);
const userService = new UserManagementService(userRepository);

/**
 * Create user mutation params
 */
interface CreateUserParams {
  command: CreateUserCommand;
  userRole: UserRole;
}

/**
 * Update user mutation params
 */
interface UpdateUserParams {
  id: string;
  command: UpdateUserCommand;
  userRole: UserRole;
}

/**
 * Delete user mutation params
 */
interface DeleteUserParams {
  id: string;
  userRole: UserRole;
}

/**
 * Assign role mutation params
 */
interface AssignRoleParams {
  id: string;
  command: AssignRoleCommand;
  userRole: UserRole;
}

/**
 * Fetch users list query hook (admin only)
 * Returns UserListItemDto with business information
 *
 * @param params - Pagination parameters
 * @param userRole - Current user's role
 * @param enabled - Whether the query should run (default: true for admins)
 */
export const useUsersQuery = (
  params?: PaginationParams,
  userRole?: UserRole,
  enabled: boolean = true
): UseQueryResult<PaginatedResult<UserListItemDto>, Error> => {
  return useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: async (): Promise<PaginatedResult<UserListItemDto>> => {
      return userService.getAllUsers(params, userRole);
    },
    staleTime: CACHE_CONFIG.USER.staleTime,
    gcTime: CACHE_CONFIG.USER.gcTime,
    enabled: enabled && userRole === 'Admin' // Only admins can fetch users
  });
};

/**
 * Create user mutation hook (admin only)
 *
 * Creates a new user and invalidates the users list cache
 */
export const useCreateUserMutation = (): UseMutationResult<void, Error, CreateUserParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ command, userRole }: CreateUserParams): Promise<void> => {
      await userService.createUser(command, userRole);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all user list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });

      // Show success notification
      notificationService.showSuccess(
        `User "${variables.command.userName}" has been created successfully`,
        'User Created'
      );
    },
    onError: (error: Error): void => {
      console.error('Create user failed:', error);
    }
  });
};

/**
 * Update user mutation hook (admin only)
 *
 * Updates an existing user and invalidates related caches
 */
export const useUpdateUserMutation = (): UseMutationResult<void, Error, UpdateUserParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, command, userRole }: UpdateUserParams): Promise<void> => {
      // userService.updateUser requires currentUserId, but we can use the same id (admin updating user)
      await userService.updateUser(id, command, userRole, id);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all user list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });

      // Invalidate the specific user detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.id) });

      // Show success notification
      notificationService.showSuccess('User has been updated successfully', 'User Updated');
    },
    onError: (error: Error): void => {
      console.error('Update user failed:', error);
    }
  });
};

/**
 * Delete user mutation hook (admin only)
 *
 * Permanently deletes a user and removes them from cache
 */
export const useDeleteUserMutation = (): UseMutationResult<void, Error, DeleteUserParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, userRole }: DeleteUserParams): Promise<void> => {
      return userService.deleteUser(id, userRole);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all user list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });

      // Remove the specific user from cache
      void queryClient.removeQueries({ queryKey: queryKeys.users.detail(variables.id) });

      // Show success notification
      notificationService.showSuccess('User has been deleted successfully', 'User Deleted');
    },
    onError: (error: Error): void => {
      console.error('Delete user failed:', error);
    }
  });
};

/**
 * Assign role mutation hook (admin only)
 *
 * Assigns a role to a user and invalidates related caches
 */
export const useAssignRoleMutation = (): UseMutationResult<void, Error, AssignRoleParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, command, userRole }: AssignRoleParams): Promise<void> => {
      await userService.assignUserRole(id, command.role, userRole);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all user list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });

      // Invalidate the specific user detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.id) });

      // Show success notification
      notificationService.showSuccess('Role has been assigned successfully', 'Role Assigned');
    },
    onError: (error: Error): void => {
      console.error('Assign role failed:', error);
    }
  });
};

/**
 * Assign business mutation params
 */
interface AssignBusinessParams {
  userId: string;
  businessId: string;
  userRole: UserRole;
}

/**
 * Remove business mutation params
 */
interface RemoveBusinessParams {
  userId: string;
  userRole: UserRole;
}

/**
 * Fetch single user by ID query hook
 * Returns UserDetailDto with complete information (for edit modal)
 *
 * @param id - User ID
 * @param userRole - Current user's role
 * @param currentUserId - Current user's ID (for access control)
 * @param enabled - Whether the query should run (default: true)
 */
export const useUserQuery = (
  id: string,
  userRole: UserRole,
  currentUserId: string,
  enabled: boolean = true
): UseQueryResult<UserDetailDto, Error> => {
  return useQuery({
    queryKey: queryKeys.users.detail(id),
    queryFn: async (): Promise<UserDetailDto> => {
      return userService.getUserById(id, userRole, currentUserId);
    },
    staleTime: CACHE_CONFIG.USER.staleTime,
    gcTime: CACHE_CONFIG.USER.gcTime,
    enabled: enabled && !!id // Only run if enabled and ID is provided
  });
};

/**
 * Assign business to user mutation hook (admin only - Sprint 010)
 *
 * Assigns a business to a user and invalidates related caches
 */
export const useAssignBusinessMutation = (): UseMutationResult<void, Error, AssignBusinessParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, businessId }: AssignBusinessParams): Promise<void> => {
      await userRepository.assignBusiness(userId, businessId);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all user list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });

      // Invalidate the specific user detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.userId) });

      // Also invalidate business details if the business has a users section
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(variables.businessId) });

      // Show success notification
      notificationService.showSuccess('Business has been assigned successfully', 'Business Assigned');
    },
    onError: (error: Error): void => {
      console.error('Assign business failed:', error);
    }
  });
};

/**
 * Remove business from user mutation hook (admin only - Sprint 010)
 *
 * Removes a business from a user and invalidates related caches
 */
export const useRemoveBusinessMutation = (): UseMutationResult<void, Error, RemoveBusinessParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }: RemoveBusinessParams): Promise<void> => {
      await userRepository.removeBusiness(userId);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all user list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });

      // Invalidate the specific user detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.userId) });

      // Invalidate all business lists in case they show linked users
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses.lists() });

      // Show success notification
      notificationService.showSuccess('Business has been removed successfully', 'Business Removed');
    },
    onError: (error: Error): void => {
      console.error('Remove business failed:', error);
    }
  });
};
